/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type CreateTherapyTypePayload,
  type TherapyType,
  type UpdateTherapyTypePayload,
  therapyTypesApi,
} from '../api/therapy-types.js';

const KEY = ['therapy-types'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useTherapyTypes(activeOnly = false): UseQueryResult<readonly TherapyType[]> {
  return useQuery({
    queryKey: [...KEY, activeOnly],
    queryFn: () => therapyTypesApi.list(activeOnly),
  });
}

export function useCreateTherapyType(): UseMutationResult<
  TherapyType,
  Error,
  CreateTherapyTypePayload
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (p: CreateTherapyTypePayload) => therapyTypesApi.create(p),
    onSuccess: invalidate,
  });
}

export function useUpdateTherapyType(): UseMutationResult<
  TherapyType,
  Error,
  { id: string; payload: UpdateTherapyTypePayload }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTherapyTypePayload }) =>
      therapyTypesApi.update(id, payload),
    onSuccess: invalidate,
  });
}
